import { useQuery } from '@tanstack/react-query';

import { loadStateOf } from '../../../../core-components/theme/components/Screen';
import { useMinuteTick } from '../../../../utilities/customHooks/useMinuteTick';
import { heldItems, heldTally } from '../../domain/feed';
import { istMinuteOfDay } from '../../domain/istClock';
import { quietClock, windowSummary } from '../../domain/quietHours';
import type { FeedItem, FeedPage, MySettings, Severity } from '../../types/domain.types';
import { myFeedQueryOptions, mySettingsQueryOptions } from '../../utils/queryOptions';

import { useLocale } from './useLocale';

export type QuietPhase =
  | { readonly kind: 'off' }
  | { readonly kind: 'active'; readonly until: string }
  | { readonly kind: 'upcoming'; readonly startsAt: string; readonly tonight: boolean };

const OFF: QuietPhase = { kind: 'off' };

const clockAt = (instant: string, locale: string): string => quietClock(istMinuteOfDay(new Date(instant)), locale);

type QuietSource = Pick<MySettings, 'quiet' | 'quietHours'>;

export const quietPhaseOf = (server: QuietSource | undefined, locale: string): QuietPhase => {
  if (server === undefined) return OFF;
  const { active, until, startsAt } = server.quiet;
  if (active && until !== null) return { kind: 'active', until: clockAt(until, locale) };
  if (startsAt === null) return OFF;
  return { kind: 'upcoming', startsAt: clockAt(startsAt, locale), tonight: windowSummary(server.quietHours).overnight };
};

export const useQuietPhase = (): QuietPhase => {
  const locale = useLocale();
  const settings = useQuery(mySettingsQueryOptions());
  return quietPhaseOf(settings.data, locale);
};

export type HeldCounts = Partial<Record<Severity, number>>;

export type QuietBanner =
  | { readonly kind: 'hidden' }
  | { readonly kind: 'holding'; readonly until: string; readonly counts: HeldCounts };

export interface QuietBannerSource {
  readonly settings: Pick<MySettings, 'emailVerified' | 'quiet'> | undefined;
  readonly page: FeedPage | undefined;
  readonly locale: string;
}

const HIDDEN: QuietBanner = { kind: 'hidden' };
const NOTHING_COUNTED: HeldCounts = {};

export const quietBannerOf = ({ settings, page, locale }: QuietBannerSource): QuietBanner => {
  if (settings === undefined || !settings.emailVerified) return HIDDEN;
  const { active, until } = page?.quiet ?? settings.quiet;
  if (!active || until === null) return HIDDEN;
  const counts = page === undefined ? NOTHING_COUNTED : heldTally(page);
  return { kind: 'holding', until: clockAt(until, locale), counts };
};

export const useQuietBanner = (): QuietBanner => {
  const locale = useLocale();
  const settings = useQuery(mySettingsQueryOptions());
  const feed = useQuery(myFeedQueryOptions());
  return quietBannerOf({ settings: settings.data, page: feed.data, locale });
};

export interface HeldAlerts {
  readonly held: readonly FeedItem[];
  readonly now: number;
  readonly banner: QuietBanner;
  readonly unknown: boolean;
}

export const useHeldAlerts = (): HeldAlerts => {
  const now = useMinuteTick();
  const locale = useLocale();
  const settings = useQuery(mySettingsQueryOptions());
  const feed = useQuery(myFeedQueryOptions());

  const feedLoad = loadStateOf(feed);
  return {
    held: heldItems(feed.data?.items ?? []),
    now,
    banner: quietBannerOf({ settings: settings.data, page: feed.data, locale }),
    unknown: !feedLoad.hasData && (feedLoad.isError || feedLoad.isPaused),
  };
};
